<template lang="pug">
    .color-kit-why-match-v2(v-if="matchType && title" :class="{ open: open }")
      template(v-if="matchType == 'customerMatch'")
        .why-title.xs-f-small.lg-f-small.customer-match
          p.max-at-tweak.upper.semi-bold You selected this color.
          button.btn-reset.cta-color-4.underline.max-at-tweak(v-if="bestMatch" @click="returnToBestMatch") Return to your best match
      
      template(v-else)
        button.why-title.btn-reset.upper.semi-bold.xs-f-xsmall.lg-f-small(type="button" @click="toggleOpen()" :class="{ open: open }")
          span.title-text.max-at-tweak.text-left {{ title }}
          mr-icon.caret-icon.xs-f-xsmall.max-at-tweak(name="caret-rounded" :fill="false" aria-hidden="true")
        
        .why-content
          .why-content-inner.xs-f-xsmall.lg-f-xsmall
            template(v-if="isPurePigment")
              p.max-at-tweak
                | Based on your Color Quiz, to
                span.semi-bold  get the cool blonde results you want
                | , we recommend this shade with an added shot of cool tone as your perfect combination.
              p.max-at-tweak.pt-100m
                | The extra 
                span.semi-bold  {{ purePigmentTone }} tone in your shade
                |  is our professional colorists' secret to help you get your coolest blonde ever.
            template(v-else-if="matchType == 'bestMatch'")
              p.max-at-tweak Based on your responses, this shade best addresses the following needs &amp; preferences:
              ul.max-at-tweak
                li.max-at-tweak(v-for="(reason, index) in bestMatchReasons" :key="index") {{ reason }}
              p.max-at-tweak Not what you had in mind?&nbsp;
                button.btn-reset.brand-color-1.underline.max-at-tweak(ref="tryItOn" @click="seeMoreColors(false, $refs.tryItOn)" :tabindex="open ? 0 : -1") Try on
                |  other colors or&nbsp;
                button.btn-reset.brand-color-1.underline.max-at-tweak(ref="more" @click="seeMoreColors(true, $refs.more)" :tabindex="open ? 0 : -1") see more colors
                | .
            template(v-else-if="matchType == 'mostPopular'")
              p.max-at-tweak This shade is a popular choice for clients who also have your current hair color, and is most likely to give you the results you are looking for.
            template(v-else-if="matchType == 'translatorMatch'")
              p.max-at-tweak This shade most closely matches the color you're currently using at home.
              button.btn-reset.brand-color-1.max-at-tweak(v-if="bestMatch" @click="returnToBestMatch") Return to your best match.
            template(v-else-if="matchType == 'coloristMatch'")
              p.max-at-tweak You spoke with a colorist who recommended this option for you.
              button.btn-reset.brand-color-1.max-at-tweak(v-if="bestMatch" @click="returnToBestMatch") Return to your best match.

</template>

<script>
  import { mapState, mapActions, mapGetters } from 'vuex';
  
  export default {
    name: "ColorKitWhyMatchV2",
    inject: ['getObjProperty'],

    props: {
      matchType: {
        type: String,
        default: ''
      },
      product: {
        type: Object,
        default() {
          return {};
        }
      }
    },

    data() {
      return {
        open: false,
        contentMap: {
          bestMatch: {
            title: "Why is this my best match?"
          },
          mostPopular: {
            title: "Why is this my most popular option?"
          },
          customerMatch: {
            title: ""
          },
          translatorMatch: {},
          coloristMatch: {}
        }
      };
    },

    computed: {
      ...mapState('customer', ['cdata']),
      ...mapState('pdp', ['tones', 'toneMap']),
      ...mapState('colorAdvisor', ['advisorCompleted']),
      ...mapGetters('pdp', ['isPurePigment', 'purePigmentTone']),

      answers() {
        return this.$root.advisorAnswers;
      },

      colorMatches() {
        return this.$root.colorMatches;
      },

      bestMatch() {
        return this.getObjProperty(this.colorMatches, 'bestMatch');
      },

      title() {
        if (this.matchType == "bestMatch") {
          return "Why is this my best match?";
        } else if (this.matchType == "mostPopular") {
          return "Why is this my most popular option?";
        } else if (this.matchType == "customerMatch") {
          return "Why is this my choice?";
        } else if (this.matchType == "translatorMatch") {
          return "Why is this the translator match?";
        } else if (this.matchType == "coloristMatch") {
          return "Why is this my colorist's choice?";
        } else {
          return "";
        }
      },

      bestMatchReasons() {
        if (!this.cdata || !this.answers) {
          return [];
        }
        var reasons = [];
        if (this.productTones[this.answers.hair_tone]) {
          reasons.push(this.toneMap[this.answers.hair_tone] + " tones");
        }
        if (this.product.highGrayCoverage) {
          reasons.push("Superior gray coverage");
        }
        if (this.answers.treated) {
          if (this.productLevel < this.answers.hair_shade) {
            reasons.push("Darker than your color-treated hair shade");
          }
          if (this.productLevel > this.answers.hair_shade) {
            reasons.push("Lighter than your color-treated hair shade");
          }
          if (this.productLevel == this.answers.hair_shade) {
            reasons.push("Same as your color-treated hair shade");
          }
        } else {
          if (this.productLevel < this.answers.natural_hair_shade) {
            reasons.push("Darker than your natural hair shade");
          }
          if (this.productLevel > this.answers.natural_hair_shade) {
            reasons.push("Lighter than your natural hair shade");
          }
          if (this.productLevel == this.answers.natural_hair_shade) {
            reasons.push("Same as your natural hair shade");
          }
        }
        return reasons;
      },

      productTones() {
        var prodTones = {};
        if (this.product.code) {
          this.product.code.split('').forEach(letter => {
            if (this.tones[letter]) {
              prodTones[this.tones[letter]] = true;
            }
          });
        }
        return prodTones;
      },

      productLevel() {
        let code = this.product.code;
        return code ? +this.product.code.match(/\d+/g)[0] : 0;
      }
    },

    watch: {
      matchType() {
        this.open = !!this.isPurePigment;
      }
    },

    mounted() {
      if (this.isPurePigment) {
        this.open = true;
      }
    },

    methods: {
      ...mapActions('pdp', ['openPlaygroundModal']),

      seeMoreColors(viewOnModel, elementRef) {
        this.openPlaygroundModal({
          props: {
            viewOnModel: viewOnModel
          },
          returnFocusElement: elementRef
        });
      },

      toggleOpen() {
        if (!this.open) {
          this.trackEvent('WhyIsItMyBestMatch: Opened');
        }
        this.open = !this.open;
      },

      returnToBestMatch() {
        this.trackEvent('ColorKitWhyMatchV2 - return to best match clicked');
        this.$router.push('/product/' + this.getObjProperty(this.bestMatch, 'url_key'));
      }
    }
  };
  </script>
  
<style scoped lang="stylus">
  .color-kit-why-match-v2
    color brand-color-1
    width 100%
    transition background-color 0.2s ease-in-out
    border-top 2px solid #7E727E33
    border-bottom 2px solid #7E727E33
  
    .why-title
      color brand-color-1
      letter-spacing 0.015rem
      padding 1em 0.5em 1em 0
      width 100%
      display flex
      justify-content space-between
      align-items center
      color brand-color-1

      &.customer-match
        display flex
        flex-direction column
        align-items start

      @media mq-desktop-plus
        &.customer-match
          flex-direction row
          justify-content space-between
  
      .caret-icon
        font-size 0.8em
        font-weight bold
        color cta-color-4
        transform rotate(-180deg)
        transition transform 350ms ease
  
    .why-content
      overflow hidden
      max-height 0
      transition max-height 400ms ease-in-out
  
      .why-content-inner
        padding-bottom 1em
  
        .why-text
          padding-bottom 0.25em
  
        ul
          list-style disc
          padding-left 1em
          padding-top 0.25em
          padding-bottom 0.5em
  
    .no-why-content
      border-top 2px solid #7E727E33
      border-bottom 2px solid #7E727E33
      padding 0.8em 0.5em
      display flex
      align-items center
      gap 1em
      width 100%
  
    &.open
      .caret-icon
        transform rotate(0deg)
  
      .why-content
        max-height 40em
        
  </style>
